import { createTheme, responsiveFontSizes } from "@mui/material/styles";
import { PaletteOptions } from "@mui/material/styles/createPalette";

const AsecosColor = {
  grey: {
    light: "#c8c8c8",
    mid: "#99958A",
    main: "#838077",
    dark: "#777777",
    deep: "#5b574d",
  },
  red: {
    light: "#b95959",
    main: "#980000",
  },
  neutrals: {
    white: "#FFFFFF",
    black: "#000000",
  },
  blue: {
    light: "#4ba3b9",
    main: "#2a7ea5",
    dark: "#004797",
  },
  black: {
    light: "#646464",
    main: "#444444",
    dark: "#000000f2",
  },
};

export const customPalette = {
  mode: "light",
  primary: {
    main: AsecosColor.red.main,
  },
  secondary: {
    main: "#707070",
  },
  background: {
    default: "#FBFBFB",
  },
} satisfies PaletteOptions;

const customTheme = createTheme({
  palette: customPalette,
  typography: {
    fontFamily: "Sofia Sans, sans-serif",
    h3: {
      fontWeight: "bold",
    },
    h6: {
      color: customPalette.secondary.main,
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "150%",
      letterSpacing: "0.021px",
    },
  },
  shape: {},

  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          "&.MuiTypography-h3": {
            color: customPalette.secondary.main,
            fontSize: "25px",
          },
          "&.MuiTypography-h1": {
            fontSize: "40px",
          },
          "&.MuiTypography-h5": {
            fontWeight: "bold",
            fontSize: "14px",
            font: "Sofia Sans",
            color: "#00000099",
          },
          "&.MuiTypography-subtitle1": {
            fontSize: "12px",
            font: "Sofia Sans",
            color: "#00000099",
          },
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          ".MuiCardContent-root": {
            ".ValueContent-value": {
              p: {
                margin: 0, //remove default margin of p if text mode = markdown
              },
            },
          },
          ".attribute-group-simple": {
            ".MuiTypography-root": {
              fontSize: "17px",
              fontWeight: "bold",
              color: customPalette.secondary.main,
            },
          },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          "&.MuiTab-root": {
            fontSize: "20px",
            textTransform: "none",
            fontWeight: "bold",
            "&[aria-selected=false]": {
              color: customPalette.secondary.main,
            },
            padding: "4px 16px",
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          top: "40px",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "1px",
          textTransform: "none",
          boxShadow: "0 0 3px rgba(0, 0, 0, 0.3)",
          transition: "all 0.3s ease",
        },
        contained: ({ theme }) => ({
          "&.MuiButton-colorPrimary": {
            "&:hover": {
              backgroundColor: theme.palette.primary.contrastText,
              color: theme.palette.primary.main,
            },
          },
        }),

        outlined: {
          borderColor: "transparent",
        },

        text: {
          boxShadow: "none",
        },
      },
    },

    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
      },
    },

    MuiRadio: {
      defaultProps: {
        disableRipple: true,
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            color: "#00000061",
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: 12,
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&.MuiInputBase-root.MuiOutlinedInput-root": {
            padding: "8px 12px",
            "input, textarea, .MuiSelect-select": {
              padding: 0,
              height: "24px",
              lineHeight: "24px",
            },
          },
          borderRadius: "0px",
        },
      },
    },

    MuiCard: {
      defaultProps: {
        square: true,
      },
      styleOverrides: {
        root: {
          boxShadow: "0px 2px 4px 0px #00000040",
          border: "2px solid #BDBDBD",
          "&.MuiPaper-elevation8": {
            borderColor: customPalette.primary.main,
          },
          "&:hover": {
            borderColor: customPalette.primary.main,
          },
        },
      },
    },

    MuiCardActionArea: {
      styleOverrides: {
        focusHighlight: {
          backgroundColor: "transparent",
        },
        root: ({ theme }) => ({
          "&.Mui-disabled": {
            color: theme.palette.action.disabled,
            opacity: 0.6,
          },
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          padding: "11px 8px",
          margin: "8px 8px 16px 8px",
          ".MuiButtonBase-root": {
            margin: "-10px",
            color: theme.palette.primary.contrastText,
          },
        }),
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          "&.MuiFormLabel-colorPrimary": {
            color: "#000000",
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        content: ({ theme }) => ({
          fontSize: "17px",
          fontWeight: "bold",
          color: theme.palette.secondary.main,
        }),
      },
    },
  },
});

export const theme = responsiveFontSizes(customTheme);
// export const theme = responsiveFontSizes(createTheme({}));
